/**
 * Models for the round-2 overworld hostiles (src/entities/overworld2):
 *  - slime: SlimeModel inner body + eyes + mouth and the translucent outer shell
 *  - cave spider: SpiderModel at 0.7x with the cave spider sheet
 *  - husk: zombie model at 1.0625x with the husk sheet
 *  - stray: skeleton model + clothing overlay sheet
 *  - drowned: modern zombie model + outer layer sheet
 *  - silverfish / endermite: segmented bug models (SilverfishModel / EndermiteModel).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat } from './core';
import { Humanoid } from './humanoid';
import { SpiderModel, type QuadPose } from './mobs';
import type { MobView } from '../../game/types';

export interface OwAnim {
  parts: THREE.Object3D;
  mats: EntityMat[];
  animate(v: MobView, q: QuadPose, headY: number, headX: number, age: number): void;
  dispose(): void;
}

const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false): CubeDef =>
  ({ tex, from, size, inflate, mirror });

// ------------------------------------------------------------------- slime

export function slimeAnim(env: EnvUniforms): OwAnim {
  const parts = new THREE.Group();
  const tex = sheetTexture('entity/slime/slime');
  const inner = entityMaterial(env, tex);
  const outer = entityMaterial(env, tex);
  // the outer shell is a translucent layer: blend with the texture alpha
  outer.transparent = true;
  outer.depthWrite = false;
  outer.fragmentShader = outer.fragmentShader.replace('gl_FragColor = vec4(col, 1.0);', 'gl_FragColor = vec4(col, tex.a);');
  const body = new THREE.Group();
  body.add(buildPart({ cubes: [cube([0, 16], [-3, 17, -3], [6, 6, 6])] }, 64, 32, inner));
  body.add(buildPart({ cubes: [cube([32, 0], [-3.25, 18, -3.5], [2, 2, 2])] }, 64, 32, inner));
  body.add(buildPart({ cubes: [cube([32, 4], [1.25, 18, -3.5], [2, 2, 2])] }, 64, 32, inner));
  body.add(buildPart({ cubes: [cube([32, 8], [0, 21, -3.5], [1, 1, 1])] }, 64, 32, inner));
  const shell = buildPart({ cubes: [cube([0, 0], [-4, 16, -4], [8, 8, 8])] }, 64, 32, outer);
  shell.renderOrder = 1;
  body.add(shell);
  parts.add(body);
  return {
    parts, mats: [inner, outer],
    animate(v) {
      const size = v.color > 0 ? v.color : 1;
      const q = Math.max(0, v.swell);
      // landing squash / take-off stretch, feet stay on the ground (y = 24)
      const sxz = 0.999 * size * (1 + 0.25 * q), sy = 0.999 * size * (1 - 0.25 * q);
      body.scale.set(sxz, sy, sxz);
      body.position.y = 24 * (1 - sy);
    },
    dispose() { inner.dispose(); outer.dispose(); },
  };
}

// ------------------------------------------------------------- cave spider

export function caveSpiderAnim(env: EnvUniforms): OwAnim {
  const m = new SpiderModel(env, 'entity/spider/cave_spider');
  m.parts.scale.setScalar(0.7);
  m.parts.position.y = 24 * (1 - 0.7);
  return {
    parts: m.parts, mats: m.mats,
    animate(_v, q) {
      m.animate(q);
      m.parts.scale.setScalar(0.7);
      m.parts.position.y = 24 * (1 - 0.7);
    },
    dispose() { for (const x of m.mats) x.dispose(); },
  };
}

// ------------------------------------------------------------ humanoid kin

interface HumanoidKind {
  sheet: string; overlay?: string; modern: boolean; thin?: boolean;
  scale?: number; tw: number; th: number; zombie: boolean;
}

function humanoidKin(env: EnvUniforms, k: HumanoidKind): OwAnim {
  const h = new Humanoid(env, { sheet: k.sheet, modern: k.modern, thin: k.thin, tw: k.tw, th: k.th });
  const o = k.overlay ? new Humanoid(env, { sheet: k.overlay, modern: k.modern, thin: k.thin, tw: k.tw, th: k.th }) : null;
  const root = new THREE.Group();
  root.add(h.root);
  if (o) {
    // clothing / outer layer: the same pose, a little bigger about the body centre
    const layer = new THREE.Group();
    layer.add(o.root);
    layer.scale.setScalar(1.07);
    layer.position.y = 12 * (1 - 1.07);
    root.add(layer);
  }
  const s = k.scale ?? 1;
  root.scale.setScalar(s);
  root.position.y = 24 * (1 - s);
  const mats = o ? [...h.mats, ...o.mats] : h.mats;
  return {
    parts: root, mats,
    animate(v, q, headY, headX, age) {
      const pose = {
        limbSwing: q.limbSwing, limbAmt: q.limbAmt, headY, headX, age, attack: v.attackAnim,
        zombieArms: k.zombie && v.aggressive, bow: !k.zombie && v.aggressive && v.heldItem !== 0,
        holding: !k.zombie && v.heldItem !== 0 && !v.aggressive,
        sneaking: false, swimming: false, bob: true,
      };
      h.animate(pose);
      o?.animate(pose);
    },
    dispose() { for (const m of mats) m.dispose(); },
  };
}

export const huskAnim = (env: EnvUniforms): OwAnim =>
  humanoidKin(env, { sheet: 'entity/zombie/husk', modern: false, tw: 64, th: 64, zombie: true, scale: 1.0625 });
export const strayAnim = (env: EnvUniforms): OwAnim =>
  humanoidKin(env, { sheet: 'entity/skeleton/stray', overlay: 'entity/skeleton/stray_overlay', modern: false, thin: true, tw: 64, th: 32, zombie: false });
export const drownedAnim = (env: EnvUniforms): OwAnim =>
  humanoidKin(env, { sheet: 'entity/zombie/drowned', overlay: 'entity/zombie/drowned_outer_layer', modern: true, tw: 64, th: 64, zombie: true });

// ------------------------------------------------------- silverfish / mite

type Seg = { size: [number, number, number]; tex: [number, number] };

function segmented(env: EnvUniforms, sheet: string, segs: Seg[], layers: { tex: [number, number]; from: [number, number, number]; size: [number, number, number]; y: number; seg: number }[], amp: number): OwAnim {
  const mat = entityMaterial(env, sheetTexture(sheet));
  const parts = new THREE.Group();
  const bodies: THREE.Group[] = [];
  let z = -3.5;
  const zs: number[] = [];
  segs.forEach((s, i) => {
    zs.push(z);
    const g = buildPart({ cubes: [cube(s.tex, [s.size[0] * -0.5, 0, s.size[2] * -0.5], s.size)], pos: [0, 24 - s.size[1], z] }, 64, 32, mat);
    parts.add(g);
    bodies.push(g);
    if (i < segs.length - 1) z += (s.size[2] + segs[i + 1].size[2]) * 0.5;
  });
  const lay = layers.map((l) => {
    const g = buildPart({ cubes: [cube(l.tex, l.from, l.size)], pos: [0, l.y, zs[l.seg]] }, 64, 32, mat);
    parts.add(g);
    return g;
  });
  return {
    parts, mats: [mat],
    animate(_v, _q, _hy, _hx, age) {
      for (let i = 0; i < bodies.length; i++) {
        bodies[i].rotation.y = Math.cos(age * 0.9 + i * 0.15 * Math.PI) * Math.PI * amp * (1 + Math.abs(i - 2));
        bodies[i].position.x = Math.sin(age * 0.9 + i * 0.15 * Math.PI) * Math.PI * 0.2 * Math.abs(i - 2);
      }
      layers.forEach((l, i) => { lay[i].rotation.y = bodies[l.seg].rotation.y; lay[i].position.x = bodies[l.seg].position.x; });
    },
    dispose() { mat.dispose(); },
  };
}

export function silverfishAnim(env: EnvUniforms): OwAnim {
  return segmented(env, 'entity/silverfish/silverfish', [
    { size: [3, 2, 2], tex: [0, 0] }, { size: [4, 3, 2], tex: [0, 4] }, { size: [6, 4, 3], tex: [0, 9] },
    { size: [3, 3, 3], tex: [0, 16] }, { size: [2, 2, 3], tex: [0, 22] }, { size: [2, 2, 2], tex: [11, 0] },
    { size: [1, 1, 2], tex: [13, 4] },
  ], [
    { tex: [20, 0], from: [-5, 0, -1.5], size: [10, 8, 3], y: 16, seg: 2 },
    { tex: [20, 11], from: [-3, 0, -1.5], size: [6, 4, 3], y: 20, seg: 4 },
    { tex: [20, 18], from: [-3, 0, -1.5], size: [6, 5, 2], y: 19, seg: 1 },
  ], 0.05);
}

export function endermiteAnim(env: EnvUniforms): OwAnim {
  return segmented(env, 'entity/endermite/endermite', [
    { size: [4, 3, 2], tex: [0, 0] }, { size: [6, 4, 5], tex: [0, 5] },
    { size: [3, 3, 1], tex: [0, 14] }, { size: [1, 2, 1], tex: [0, 18] },
  ], [], 0.01);
}

// -------------------------------------------------------------------- wolf

/** Vanilla dye colours (DyeColor.textureDiffuseColor), index 0 = white .. 15 = black. */
export const DYE_RGB = [
  0xf9fffe, 0xf9801d, 0xc74ebd, 0x3ab3da, 0xfed83d, 0x80c71f, 0xf38baa, 0x474f52,
  0x9d9d97, 0x169c9c, 0x8932b8, 0x3c44aa, 0x835432, 0x5e7c16, 0xb02e26, 0x1d1d21,
];

/**
 * WolfModel (64x32): head + ears + nose, body, mane, four legs, tail. State comes
 * from `view.color` bits (see entities/overworld2/wolf.ts): collar 0-15, tamed 16,
 * sitting 32; `view.aggressive` swaps in the angry eyes sheet.
 */
export function wolfAnim(env: EnvUniforms): OwAnim {
  const mat = entityMaterial(env, sheetTexture('entity/wolf/wolf'));
  // the collar is the vanilla wolf_collar sheet over a second, slightly inflated pass, tinted by the dye colour
  const collarMat = entityMaterial(env, sheetTexture('entity/wolf/wolf_collar'));
  collarMat.transparent = true; collarMat.depthWrite = false;
  const parts = new THREE.Group();
  const make = (m: EntityMat, g: number) => {
    const root = new THREE.Group();
    parts.add(root);
    const P = (d: Parameters<typeof buildPart>[0], parent: THREE.Object3D = root): THREE.Group => {
      const n = buildPart(d, 64, 32, m); parent.add(n); return n;
    };
    const head = P({ cubes: [cube([0, 0], [-2, -3, -2], [6, 6, 4], g)], pos: [-1, 13.5, -7] });
    P({ cubes: [cube([16, 14], [-2, -5, 0], [2, 2, 1], g)] }, head);
    P({ cubes: [cube([16, 14], [2, -5, 0], [2, 2, 1], g)] }, head);
    P({ cubes: [cube([0, 10], [-0.5, 0, -5], [3, 3, 4], g)] }, head);
    const body = P({ cubes: [cube([18, 14], [-3, -2, -3], [6, 9, 6], g)], pos: [0, 14, 2], rot: [Math.PI / 2, 0, 0] });
    const mane = P({ cubes: [cube([21, 0], [-3, -3, -3], [8, 6, 7], g)], pos: [-1, 14, -3], rot: [Math.PI / 2, 0, 0] });
    const leg = (x: number, z: number): THREE.Group => P({ cubes: [cube([0, 18], [0, 0, -1], [2, 8, 2], g)], pos: [x, 16, z] });
    const rh = leg(-2.5, 7), lh = leg(0.5, 7), rf = leg(-2.5, -4), lf = leg(0.5, -4);
    const tail = P({ cubes: [cube([9, 18], [0, 0, -1], [2, 8, 2], g)], pos: [-1, 12, 8] });
    return { root, head, body, mane, rh, lh, rf, lf, tail };
  };
  const layers = [make(mat, 0), make(collarMat, 0.02)];
  const wild = sheetTexture('entity/wolf/wolf'), tameTex = sheetTexture('entity/wolf/wolf_tame'), angryTex = sheetTexture('entity/wolf/wolf_angry');
  const c = new THREE.Color();
  return {
    parts, mats: [mat, collarMat],
    animate(v, q, headY, headX) {
      const tame = (v.color & 16) !== 0, sit = (v.color & 32) !== 0;
      mat.uniforms.uMap.value = v.aggressive ? angryTex : tame ? tameTex : wild;
      collarMat.uniforms.uTint.value.copy(c.setHex(DYE_RGB[v.color & 15]));
      layers[1].root.visible = tame;
      const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.4;
      for (const { head, body, mane, rh, lh, rf, lf, tail } of layers) {
        head.rotation.set(headX, headY, 0);
        // vanilla standing pose
        body.position.set(0, 14, 2); body.rotation.x = Math.PI / 2;
        mane.position.set(-1, 14, -3); mane.rotation.x = Math.PI / 2;
        rh.position.set(-2.5, 16, 7); lh.position.set(0.5, 16, 7); rf.position.set(-2.5, 16, -4); lf.position.set(0.5, 16, -4);
        tail.position.set(-1, 12, 8);
        if (sit) {
          mane.position.set(-1, 16, -3); mane.rotation.x = Math.PI * 0.4;
          body.position.set(0, 18, 0); body.rotation.x = Math.PI * 0.25;
          tail.position.set(-1, 21, 6);
          rh.position.set(-2.5, 22.7, 2); lh.position.set(0.5, 22.7, 2);
          rh.rotation.x = lh.rotation.x = Math.PI * 1.5;
          rf.rotation.x = lf.rotation.x = 5.811946;
          rf.position.set(-2.49, 17, -4); lf.position.set(0.51, 17, -4);
          tail.rotation.x = Math.PI * 0.5 + 0.35;
        } else {
          rh.rotation.x = Math.cos(s) * a; lh.rotation.x = Math.cos(s + Math.PI) * a;
          rf.rotation.x = Math.cos(s + Math.PI) * a; lf.rotation.x = Math.cos(s) * a;
          tail.rotation.x = tame ? Math.PI * 0.45 : Math.PI / 5;
        }
      }
    },
    dispose() { mat.dispose(); collarMat.dispose(); },
  };
}
